import * as React from 'react';
import { canManagePeople, IMembership, IPortalMe, setActiveClient, signOut, stopViewAs } from './auth';
import { BrandLockup, Icon } from './brand';

// Signed-in chrome: brand, app breadcrumb, nav, user menu.

export const navigate = (path: string): void => {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
};

export const Link: React.FunctionComponent<{ to: string; className?: string; style?: React.CSSProperties; children?: React.ReactNode }> = (p) => (
  <a href={p.to} className={p.className} style={p.style} onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) { return; }
    e.preventDefault();
    navigate(p.to);
  }}>{p.children}</a>
);

const initials = (name: string, email: string): string => {
  const src: string = name.trim() !== '' ? name : email;
  const parts: string[] = src.split(/[\s.@_-]+/).filter((x: string): boolean => x !== '');
  return ((parts[0] || '?').charAt(0) + (parts.length > 1 ? parts[1].charAt(0) : '')).toUpperCase();
};

const UserMenu: React.FunctionComponent<{ me: IPortalMe }> = ({ me }) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect((): (() => void) => {
    const close = (e: MouseEvent): void => { if (ref.current !== null && !ref.current.contains(e.target as Node)) { setOpen(false); } };
    document.addEventListener('mousedown', close);
    return (): void => document.removeEventListener('mousedown', close);
  }, []);
  return (
    <div className="bu-user" ref={ref}>
      <button className="bu-user-btn" onClick={(): void => setOpen(!open)} aria-expanded={open}>
        <span className="bu-avatar">{initials(me.name, me.email)}</span>
        <span>{me.client !== null ? me.client.name : 'Account'}</span>
      </button>
      {open && (
        <div className="bu-menu" role="menu">
          <div className="bu-menu-head">
            <strong>{me.name || me.email}</strong>
            <span>{me.email}</span>
            <span style={{ marginTop: 8 }}>
              <span className="bu-chip">{me.provider === 'google' ? 'Google Workspace' : 'Microsoft 365'}</span>
              {me.role !== null && <span className="bu-chip" style={{ marginLeft: 6 }}>{me.role.name}</span>}
            </span>
          </div>
          {me.memberships.length > 1 && (
            <div className="bu-menu-orgs">
              <div className="bu-side-label" style={{ padding: '8px 14px 4px' }}>Switch organization</div>
              {me.memberships.map((m: IMembership) => (
                <button key={m.tenantId} className={me.client !== null && me.client.tenantId === m.tenantId ? 'on' : ''}
                  onClick={(): void => { setActiveClient(m.tenantId); window.location.assign('/apps'); }}>
                  <Icon name={me.client !== null && me.client.tenantId === m.tenantId ? 'check' : 'building'} size={17} />
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.25 }}>{m.name}<small style={{ color: 'var(--text-3)', fontWeight: 500 }}>{m.roleName}</small></span>
                </button>
              ))}
            </div>
          )}
          <Link to="/apps"><Icon name="grid" size={17} /> My apps</Link>
          {canManagePeople(me) && <Link to="/admin/team"><Icon name="users" size={17} /> People &amp; roles</Link>}
          {me.isOwner && <Link to="/admin/clients"><Icon name="building" size={17} /> Clients</Link>}
          <button onClick={signOut}><Icon name="logout" size={17} /> Sign out</button>
        </div>
      )}
    </div>
  );
};

export const Shell: React.FunctionComponent<{ me: IPortalMe; crumb?: string; active?: string; children?: React.ReactNode }> = (p) => (
  <div style={{ minHeight: '100vh' }}>
    <header className="bu-topbar">
      <div className="bu-topbar-inner">
        <Link to="/apps" className="bu-brand"><BrandLockup /></Link>
        {p.crumb !== undefined && <span className="bu-crumb">{p.crumb}</span>}
        <nav className="bu-nav">
          <Link to="/apps" className={p.active === 'apps' ? 'active' : ''}>Apps</Link>
          {canManagePeople(p.me) && <Link to="/admin/team" className={p.active === 'admin' ? 'active' : ''}>Admin</Link>}
        </nav>
        <span className="bu-spacer" />
        <UserMenu me={p.me} />
      </div>
    </header>
    {p.me.viewedBy && (
      <div role="status" style={{ position: 'sticky', top: 0, zIndex: 40, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', background: '#fff7e0', borderBottom: '1px solid #f0cf73', color: '#6b4b00', fontSize: 14 }}>
        <span><b>Viewing as {p.me.name}</b> ({p.me.role ? p.me.role.name : 'no role'}, {p.me.client ? p.me.client.name : ''}) — read-only, nothing you do here is saved.</span>
        <button className="bu-btn bu-btn-primary bu-btn-sm" onClick={stopViewAs}>Stop viewing</button>
      </div>
    )}
    {p.children}
  </div>
);
